import { useQuery } from '@apollo/react-hooks';
import { Queries } from 'gql';
import { LoggerFactory } from 'logger';
import Metro from 'model/Metro';
import OnRamp from 'model/OnRamp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import DocumentTitle from 'react-document-title';
import qs from 'qs';
import { withRouter } from 'react-router-dom';
import ObjectUtils from 'utils/ObjectUtils';
import Page from 'utils/Page';
import { toast } from 'utils/Toast';
import { useMarketplaceSegments, withMarketplaceProvider } from 'providers/MarketplaceProvider';
import { useCurrentProject, withLiteProjectProvider } from 'providers/LiteProjectProvider';
import LiteProjectDetailViewHeader from 'views/components/Header/LiteProjectDetailViewHeader';
import LitePanel from 'views/components/PhasePanel/LitePanel';
import ProjectMap from 'views/components/ProjectMap';
import { getMapStyle, getMapStyleUrl } from 'views/components/ProjectMap/helpers/storeMapStyle';
import UserView from './UserView';
import './ProjectDetailView/ProjectDetailView.scss';
import './ProjectDetailView/Sidebar.scss';

const defaultLayerConfiguration = {
  activeMetros: true,
  inactiveMetros: true,
  customerLocations: true,
  services: true,
  activeRegions: true,
  inactiveRegions: true,
  latencies: true,
  connections: true,
  directConnections: true,
  latencyRings: false,
  availableRegions: true,
  customerLocationLines: true,
  labelSectionEnabled: true,
  labels: true,
  metroLabels: false,
  locationLabels: false,
  regionLabels: false,
  infraComponents: true,
  capOnRampEnabled: true,
};

const defaultCapacityConfiguration = {
  datacenters: false,
  cabe: null,
  kva: null,
  cooling: false,
  metro: 'total',
  cabinet: 'average',
};

const LiteCallbackContent = ({ user }) => {
  const { projectId, project, phase, phaseLoading, projectLoading, isEditing, changePhase } = useCurrentProject();
  const { marketplaceSegments } = useMarketplaceSegments();
  const { data: metrosData, loading: metrosLoading, error: metrosError } = useQuery(Queries.GET_LITE_METROS);
  const { data: onRampsData } = useQuery(Queries.GET_ON_RAMP_CLOUDS);
  const { data: layersData } = useQuery(Queries.GET_LAYERS_CONFIGURATION, { variables: { phaseId: phase?.id } });

  const [metros, setMetros] = useState(null);
  const [connectionType, setConnectionType] = useState('topological');
  const [infraComponentCard, setInfraComponentCard] = useState('stacked');
  const [ringLatency, setRingLatency] = useState(5);
  const [ringColor, setRingColor] = useState('red');
  const [availableRegion, setAvailableRegion] = useState('all');
  const [metroType, setMetroType] = useState('all');
  const [platform] = useState(null);
  const [platformMetros] = useState(null);
  const [product] = useState(null);
  const [isSidebarCollapsed] = useState(false);
  const [zoom] = useState();
  const [cloudsList, setCloudsList] = useState(null);
  const [onRamps, setOnRamps] = useState(null);
  const [layers, setLayers] = useState(defaultLayerConfiguration);
  const [onRampConfiguration, setOnRampConfiguration] = useState({
    showHeatMap: true,
    showCount: false,
    showMarketShare: false,
  });
  const [capacityConfiguration, setCapacityConfiguration] = useState(defaultCapacityConfiguration);
  const [mapUrl, setMapUrl] = useState(getMapStyleUrl(getMapStyle()));
  const [selectedMapStyle, setSelectedMapStyle] = useState(getMapStyle());
  const [selectedOnRamps, setSelectedOnRamps] = useState([]);
  const [openMapMenu, setOpenMapMenu] = useState(null);
  const [mapType, setMapType] = useState('heatmap');
  const [selectedLayer, setSelectedLayer] = useState(null);
  const [capOnRamp, setCapOnRamp] = useState('all');
  const [marketplaceSegment, setMarketplaceSegment] = useState(null);
  const [selectedEms, setSelectedEms] = useState(null);
  const [sectionMapFocus, setSectionMapFocus] = useState(null);

  const loading = useMemo(() => projectLoading || metrosLoading || !phase || !project, [projectLoading, metrosLoading, project, phase]);
  const error = useMemo(() => !loading && metrosError, [loading, metrosError]);
  const onRampsMetros = onRamps?.filter((onRamp) => selectedOnRamps?.some((selected) => selected === onRamp.cloud));

  useEffect(() => {
    if (!metrosData) return;
    setMetros(Metro.fromJSONArray(metrosData.metros));
  }, [metrosData]);

  useEffect(() => {
    if (!onRampsData) return;
    const allOnRamps = OnRamp.fromJSONArray(onRampsData.getOnRampClouds.flat());
    setOnRamps(allOnRamps);
    setCloudsList([...new Set(allOnRamps.map((onRamp) => onRamp.cloud))]);
  }, [onRampsData]);

  useEffect(() => {
    if (!layersData) return;
    setLayers((prevLayers) => ({
      ...prevLayers,
      ...{
        metroLabels: layersData.getLayersConfiguration.metroLabels,
        connections: layersData.getLayersConfiguration.connections,
        inactiveMetros: layersData.getLayersConfiguration.inactiveMetros,
        latencyRings: layersData.getLayersConfiguration.latencyRings,
        availableRegions: layersData.getLayersConfiguration.availableRegions,
      },
    }));
    setRingLatency(layersData.getLayersConfiguration.ringLatency);
    setRingColor(layersData.getLayersConfiguration.ringColor);
    setConnectionType(layersData.getLayersConfiguration.connectionType);
    setCapOnRamp(layersData.getLayersConfiguration.capOnRamp);
    setOnRampConfiguration({
      showHeatMap: layersData.getLayersConfiguration.showHeatMap,
      showCount: layersData.getLayersConfiguration.showCount,
      showMarketShare: layersData.getLayersConfiguration.showMarketShare,
    });
    setSelectedOnRamps(layersData.getLayersConfiguration.selectedOnRamps);
  }, [layersData]);

  useEffect(() => {
    if (!layersData) return;
    const savedSegmentId = layersData.getLayersConfiguration.marketplaceSegment;
    if (!savedSegmentId) {
      setMarketplaceSegment(null);
      return;
    }
    const matchedSegment = marketplaceSegments.find((segment) => ObjectUtils.equals(segment.id, savedSegmentId)) || null;
    setMarketplaceSegment(matchedSegment);
  }, [layersData, marketplaceSegments]);

  const _onSectionMapFocusChange = useCallback((focus) => {
    setSectionMapFocus((prev) => {
      if (prev?.sourceKey === focus?.sourceKey) {
        return prev;
      }
      return focus;
    });
  }, []);

  const _toggleLayer = (layer) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setLayers((prev) => ({ ...prev, [layer]: !prev[layer] }));
  };

  const _toggleAvailableRegions = (layer) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (!layers[layer]) {
      setAvailableRegion('all');
      setLayers((prev) => ({
        ...prev,
        availableRegions: true,
        activeRegions: true,
        inactiveRegions: true,
      }));
    } else {
      setLayers((prev) => ({ ...prev, availableRegions: false, activeRegions: false, inactiveRegions: false }));
    }
  };

  const toggleLabel = (layer) => (e) => {
    if (e && e.stopPropagation) e.stopPropagation();

    setLayers((prev) => {
      const newLayer = { ...prev, [layer]: !prev[layer] };
      if (layer === 'labelSectionEnabled' && !prev.labelSectionEnabled && !prev.labels && !prev.metroLabels && !prev.locationLabels && !prev.regionLabels) {
        return { ...prev, labelSectionEnabled: true, labels: true };
      }
      if (!newLayer.labels && !newLayer.metroLabels && !newLayer.locationLabels && !newLayer.regionLabels && prev.labelSectionEnabled) {
        return { ...newLayer, labelSectionEnabled: false };
      }
      return newLayer;
    });
  };

  const _setAvailableRegion = (region) => {
    if (region === 'all') {
      setAvailableRegion(region);
      setLayers((prev) => ({ ...prev, activeRegions: true, inactiveRegions: true }));
    } else {
      setAvailableRegion(region);
      setLayers((prev) => ({ ...prev, activeRegions: true, inactiveRegions: false }));
    }
  };

  if (error) {
    return (
      <DocumentTitle title={Page.title('Error! | Projects')}>
        <p style={{ margin: '100px' }}>ERROR! {error.message}</p>
      </DocumentTitle>
    );
  }

  if (loading) {
    return (
      <DocumentTitle title={Page.title('(Loading) | Projects')}>
        <div className="loading-container">
          <span className="loading-text">
            <div className="spinner"></div>
            Loading Project
          </span>
        </div>
      </DocumentTitle>
    );
  }

  return (
    <DocumentTitle title={Page.title(`${project.name} | Projects`)}>
      <DndProvider backend={Backend}>
        <div className="app">
          <LiteProjectDetailViewHeader user={user} onChangePhase={changePhase} onReorderPhases={() => {}} onOpenPhasesModals={() => {}} />

          <div className="right-sidebar">
            <LitePanel onRampsMetros={onRampsMetros} metros={metros} onMapFocusChange={_onSectionMapFocusChange} marketplaceSegment={marketplaceSegment} />
          </div>

          <ProjectMap
            phaseId={phase.id}
            projectId={projectId}
            project={project}
            phase={phase}
            metros={metros}
            isInfoOpen={false}
            onSelect={() => {}}
            onEnableRegion={() => {}}
            onDisableRegion={() => {}}
            onManageMetros={() => {}}
            selected={null}
            selectedRegions={[]}
            connectionType={connectionType}
            setConnectionType={setConnectionType}
            setInfraComponentCard={setInfraComponentCard}
            infraComponentCard={infraComponentCard}
            layers={layers}
            toggleLayer={_toggleLayer}
            ringLatency={ringLatency}
            setRingLatency={setRingLatency}
            ringColor={ringColor}
            setRingColor={setRingColor}
            availableRegion={availableRegion}
            onMetroPanelTabChange={() => {}}
            setAvailableRegion={_setAvailableRegion}
            metroType={metroType}
            setMetroType={setMetroType}
            toggleAvailableRegions={_toggleAvailableRegions}
            platform={platform}
            platformMetros={platformMetros}
            product={product}
            onClose={() => {}}
            isSidebarCollapsed={isSidebarCollapsed}
            openMapMenu={openMapMenu}
            changeOpenMapMenu={setOpenMapMenu}
            mapType={mapType}
            capOnRamp={capOnRamp}
            setCapOnRamp={setCapOnRamp}
            marketplaceSegment={marketplaceSegment}
            onChangeMapType={setMapType}
            onChangeMarketplaceSegment={() => {}}
            isEditing={isEditing}
            isLite
            zoom={zoom}
            toggleLabel={toggleLabel}
            cloudsList={cloudsList}
            onRamps={onRamps}
            onRampConfiguration={onRampConfiguration}
            capacityConfiguration={capacityConfiguration}
            selectedOnRamps={selectedOnRamps}
            setOnRampConfiguration={setOnRampConfiguration}
            setCapacityConfiguration={setCapacityConfiguration}
            setSelectedOnRamps={setSelectedOnRamps}
            isCapacityAllowed={false}
            selectedLayer={selectedLayer}
            setSelectedLayer={setSelectedLayer}
            mapUrl={mapUrl}
            setMapUrl={setMapUrl}
            selectedMapStyle={selectedMapStyle}
            setSelectedMapStyle={setSelectedMapStyle}
            defaultLayerConfiguration={defaultLayerConfiguration}
            defaultCapacityConfiguration={defaultCapacityConfiguration}
            selectedEms={selectedEms}
            setSelectedEms={setSelectedEms}
            sectionMapFocus={sectionMapFocus}
            loading={phaseLoading}
          />
        </div>
      </DndProvider>
    </DocumentTitle>
  );
};

const LiteCallbackContentWithProviders = withLiteProjectProvider(withMarketplaceProvider(LiteCallbackContent));

class LiteCallbackView extends UserView {
  constructor(props) {
    super(props);
    this._logger = LoggerFactory.getLogger(this);
  }

  componentDidMount() {
    // If user is already authenticated, render lite project directly in this view.
    if (this.isAuthenticated) {
      const { projectId } = this.props.match.params;
      this._logger.debug(`User authenticated, redirecting to /lite/${projectId}`);
      // this.props.history.replace(`/lite/${projectId}`);
      return;
    }

    if (this.state.errors.length > 0) {
      this._logger.warn('Errors exist, redirecting to /');
      this.props.history.replace('/');
      return;
    }

    //parse the query string
    const q = qs.parse(this.props.location.search.substring(1));

    //if the login verification failed, we will get ?error=invalid_code
    if (q.error) {
      this._logger.error(`There was an error logging in: ${q.error}`);
      toast('Authorization code is not valid or has expired', 'error');
      this.props.history.replace('/');
      return;
    }

    this._logger.debug(`Callback received from lite access service`);

    if (!q.authorization_code) {
      this._logger.warn('No authorization code received, redirecting to /');
      this.props.history.replace('/');
      return;
    }

    this.login(q.authorization_code);
  }

  render() {
    if (this.isAuthenticated) {
      return <LiteCallbackContentWithProviders user={this.user} />;
    }

    return null;
  }
}

export default withRouter(LiteCallbackView);
